import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Checkbox,
  ListItemText,
  TextField,
  ListSubheader,
} from '@mui/material';
import { Option } from '../../../models/component-models/ComponentModels';

interface MultiSelectProps {
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value,
  onChange,
  label,
  placeholder,
  error,
  disabled,
}) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [menuWidth, setMenuWidth] = React.useState<number | undefined>(undefined);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  const selectId =
    label || placeholder
      ? `${(label || placeholder)!.replace(/\s+/g, '-').toLowerCase()}-multiselect`
      : undefined;

  const filteredOptions = React.useMemo(
    () => options.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase())),
    [options, searchTerm]
  );

  const handleChange = (event: any) => {
    const newValue = event.target.value;
    // Material-UI Select multiple modunda string[] döner
    onChange(typeof newValue === 'string' ? newValue.split(',') : newValue);
  };

  const renderValue = (selected: string[]) => {
    if (selected.length === 0) {
      return placeholder || '';
    }
    if (selected.length === 1) {
      const option = options.find(opt => opt.value === selected[0]);
      return option?.label || selected[0];
    }
    return `${selected.length} seçili`;
  };

  return (
    <FormControl
      ref={anchorRef}
      fullWidth
      error={!!error}
      disabled={disabled}
      variant="outlined"
      size="small"
    >
      {(label || placeholder) && (
        <InputLabel id={`${selectId}-label`}>{label || placeholder}</InputLabel>
      )}
      <Select
        labelId={`${selectId}-label`}
        id={selectId}
        multiple
        value={value}
        onChange={handleChange}
        label={label || placeholder}
        renderValue={renderValue}
        onOpen={() => setMenuWidth(anchorRef.current?.offsetWidth ?? undefined)}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
              width: menuWidth,
              maxWidth: menuWidth,
            },
          },
        }}
      >
        <ListSubheader
          sx={{
            px: 1,
            py: 0.25,
            lineHeight: 1,
          }}
        >
          <TextField
            autoFocus
            fullWidth
            size="small"
            variant="standard"
            placeholder="Ara..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onKeyDown={e => {
              e.stopPropagation();
            }}
            InputProps={{
              style: { paddingTop: 2, paddingBottom: 2 },
            }}
          />
        </ListSubheader>
        {filteredOptions.map(option => (
          <MenuItem
            key={option.value}
            value={option.value}
            sx={{
              py: 0.25,
              minHeight: 36,
              borderBottom: '1px solid rgba(0,0,0,0.06)',
              '&:last-of-type': {
                borderBottom: 'none',
              },
            }}
          >
            <Checkbox
              checked={value.includes(option.value)}
              sx={{
                color: '#16a34a',
                '&.Mui-checked': {
                  color: '#16a34a',
                },
              }}
            />
            <ListItemText
              primary={option.label}
              primaryTypographyProps={{ noWrap: true }}
            />
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};
